import React from "react";
import jsPDF from "jspdf";
import { QuizResult } from "../../shared/types";
import { BusinessAuditRecommendation } from "./features";

interface PDFExportProps {
  results: QuizResult[];
  recommendations: BusinessAuditRecommendation[];
  resultsRef: React.RefObject<HTMLDivElement>;
}

const BusinessAuditPDFExport: React.FC<PDFExportProps> = ({
  results,
  recommendations,
  resultsRef,
}) => {
  // Helper functions
  const calculateRiskScore = (results: QuizResult[]): number => {
    let totalScore = 0;
    results.forEach((result) => {
      const answer = result.answer.toLowerCase();
      if (
        answer.includes("strong") ||
        answer.includes("consistently") ||
        answer.includes("crystal clear") ||
        answer.includes("highly") ||
        answer.includes("extremely") ||
        answer.includes("comprehensive") ||
        answer.includes("quickly") ||
        answer.includes("energized")
      ) {
        totalScore += 10;
      } else if (
        answer.includes("mostly") ||
        answer.includes("generally") ||
        answer.includes("fairly") ||
        answer.includes("good") ||
        answer.includes("usually") ||
        answer.includes("positive")
      ) {
        totalScore += 7;
      } else if (
        answer.includes("some") ||
        answer.includes("mix") ||
        answer.includes("basic") ||
        answer.includes("sometimes") ||
        answer.includes("stressed")
      ) {
        totalScore += 4;
      } else {
        totalScore += 1;
      }
    });
    return Math.round((totalScore / (results.length * 10)) * 100);
  };

  const getUrgencyLevel = (score: number): string => {
    if (score >= 80) return "Low - Optimization Opportunities";
    if (score >= 60) return "Medium - Strategic Improvements Needed";
    if (score >= 40) return "High - Significant Issues to Address";
    return "Critical - Immediate Action Required";
  };

  const exportToPDF = async () => {
    const currentYear = new Date().getFullYear();
    try {
      const button = document.querySelector(
        "[data-pdf-export]"
      ) as HTMLButtonElement;
      if (button) {
        button.disabled = true;
        button.innerHTML = "Generating PDF...";
      }

      const pdf = new jsPDF("p", "mm", "a4");
      const pageWidth = 210;
      const pageHeight = 297;
      const margin = 15;
      const contentWidth = pageWidth - 2 * margin;

      // Helper function to add a new page if needed
      const checkPageBreak = (currentY: number, neededHeight: number) => {
        if (currentY + neededHeight > pageHeight - margin) {
          pdf.addPage();
          return margin;
        }
        return currentY;
      };

      // Helper function to add footer with branding to each page
      const addFooter = () => {
        pdf.setFillColor(21, 42, 83);
        pdf.rect(0, pageHeight - 25, pageWidth, 25, "F");
        pdf.setTextColor(255, 255, 255);
        pdf.setFontSize(8);
        pdf.text(
          `© ${currentYear} Inthera Consulting Group. All rights reserved.`,
          pageWidth / 2,
          pageHeight - 15,
          { align: "center" }
        );
        pdf.text(
          "Visit us at: intheraconsultinggroup.com",
          pageWidth / 2,
          pageHeight - 8,
          { align: "center" }
        );
        pdf.link(pageWidth / 2 - 25, pageHeight - 12, 50, 8, {
          url: "https://intheraconsultinggroup.com/",
        });
      };

      // Load the logo image
      const loadLogoImage = (): Promise<string> => {
        return new Promise((resolve, reject) => {
          const img = new Image();
          img.crossOrigin = "anonymous";
          img.onload = () => {
            const canvas = document.createElement("canvas");
            const ctx = canvas.getContext("2d");
            if (ctx) {
              canvas.width = img.width;
              canvas.height = img.height;
              ctx.drawImage(img, 0, 0);
              resolve(canvas.toDataURL("image/png"));
            } else {
              reject(new Error("Could not get canvas context"));
            }
          };
          img.onerror = () => reject(new Error("Could not load logo"));
          img.src = "/assets/inthera-logo.png";
        });
      };

      let logoDataUrl: string | null = null;
      try {
        logoDataUrl = await loadLogoImage();
      } catch (error) {
        console.warn("Could not load logo:", error);
      }

      // Enhanced Title Page with gradient effect simulation
      pdf.setFillColor(21, 42, 83);
      pdf.rect(0, 0, pageWidth, 80, "F");

      // Add Inthera logo if available
      if (logoDataUrl) {
        try {
          pdf.addImage(logoDataUrl, "PNG", pageWidth - 50, 10, 35, 20);
        } catch (error) {
          console.warn("Could not add logo to PDF:", error);
        }
      }

      // Add decorative accent strip
      pdf.setFillColor(201, 167, 80);
      pdf.rect(0, 75, pageWidth, 5, "F");

      // Main title with improved positioning
      pdf.setTextColor(255, 255, 255);
      pdf.setFontSize(28);
      pdf.text("Business Performance Audit Report", pageWidth / 2, 45, {
        align: "center",
      });

      // Subtitle styling
      pdf.setFontSize(14);
      pdf.setTextColor(200, 200, 200);
      pdf.text("Comprehensive Business Health Analysis", pageWidth / 2, 58, {
        align: "center",
      });

      // Generation date with better styling
      pdf.setFontSize(10);
      pdf.setTextColor(180, 180, 180);
      pdf.text(
        `Generated on ${new Date().toLocaleDateString("en-US", {
          weekday: "long",
          year: "numeric",
          month: "long",
          day: "numeric",
        })}`,
        pageWidth / 2,
        68,
        { align: "center" }
      );

      let yPosition = 100;

      // Enhanced Executive Summary Section
      // Section header with decorative styling
      pdf.setFillColor(248, 250, 252);
      pdf.rect(margin, yPosition - 5, contentWidth, 30, "F");
      pdf.setFillColor(201, 167, 80);
      pdf.rect(margin, yPosition - 5, 4, 30, "F");

      pdf.setTextColor(21, 42, 83);
      pdf.setFontSize(20);
      pdf.text("Executive Summary", margin + 10, yPosition + 8);

      // Summary content with better styling
      pdf.setTextColor(55, 65, 81);
      pdf.setFontSize(12);
      const summaryText = `This comprehensive assessment analyzes your business across 10 critical dimensions using our proprietary IN.8 Framework™. We've identified ${recommendations.length} key areas for improvement that can significantly impact your business growth and profitability.`;
      const summaryLines = pdf.splitTextToSize(summaryText, contentWidth - 20);
      pdf.text(summaryLines, margin + 10, yPosition + 18);
      yPosition += 40;

      // Enhanced Business Health Score Section
      const riskScore = calculateRiskScore(results);
      const urgencyLevel = getUrgencyLevel(riskScore);

      // Modern card-style design for health score
      pdf.setFillColor(255, 255, 255);
      pdf.rect(margin, yPosition, contentWidth, 35, "F");
      pdf.setDrawColor(230, 230, 230);
      pdf.setLineWidth(0.5);
      pdf.rect(margin, yPosition, contentWidth, 35);

      // Score circle background
      const scoreColor =
        riskScore >= 70
          ? [34, 197, 94]
          : riskScore >= 40
          ? [245, 158, 11]
          : [220, 38, 38];
      pdf.setFillColor(scoreColor[0], scoreColor[1], scoreColor[2]);
      pdf.circle(margin + 25, yPosition + 17, 12, "F");

      // Score text in circle
      pdf.setTextColor(255, 255, 255);
      pdf.setFontSize(16);
      pdf.text(`${riskScore}`, margin + 25, yPosition + 19, {
        align: "center",
      });

      // Health score label and description
      pdf.setTextColor(21, 42, 83);
      pdf.setFontSize(16);
      pdf.text("Business Health Score", margin + 45, yPosition + 12);

      pdf.setTextColor(75, 85, 99);
      pdf.setFontSize(11);
      pdf.text(`Priority Level: ${urgencyLevel}`, margin + 45, yPosition + 22);

      // Add visual indicator bar
      const barWidth = 80;
      const barHeight = 4;
      const barX = margin + 45;
      const barY = yPosition + 27;

      // Background bar
      pdf.setFillColor(229, 231, 235);
      pdf.rect(barX, barY, barWidth, barHeight, "F");

      // Progress bar
      const progressWidth = (riskScore / 100) * barWidth;
      pdf.setFillColor(scoreColor[0], scoreColor[1], scoreColor[2]);
      pdf.rect(barX, barY, progressWidth, barHeight, "F");

      // Start new page for Analytics Dashboard
      pdf.addPage();
      yPosition = margin;

      // Enhanced Charts Section

      // Section header with icon
      pdf.setFillColor(248, 250, 252);
      pdf.rect(margin, yPosition - 5, contentWidth, 25, "F");
      pdf.setFillColor(201, 167, 80);
      pdf.rect(margin, yPosition - 5, 4, 25, "F");

      pdf.setTextColor(21, 42, 83);
      pdf.setFontSize(18);
      pdf.text("Analytics Dashboard", margin + 10, yPosition + 8);

      pdf.setTextColor(75, 85, 99);
      pdf.setFontSize(10);
      pdf.text(
        "Visual representation of your business metrics",
        margin + 10,
        yPosition + 16
      );
      yPosition += 30;

      try {
        // Add a small delay to ensure charts are fully rendered
        await new Promise((resolve) => setTimeout(resolve, 500));

        const chartContainers =
          resultsRef.current?.querySelectorAll("[data-chart]");
        if (chartContainers && chartContainers.length > 0) {
          let chartCount = 0;
          for (const container of Array.from(chartContainers)) {
            const chartCanvas = container.querySelector("canvas");
            if (chartCanvas && chartCount < 4) {
              // Add small delay between each chart capture
              await new Promise((resolve) => setTimeout(resolve, 100));

              if (chartCount % 2 === 0 && chartCount > 0) yPosition += 85;
              yPosition = checkPageBreak(yPosition, 85);

              const chartDataUrl = chartCanvas.toDataURL("image/png", 0.9);
              const chartWidth = 85;
              const chartHeight = 65;
              const chartX = margin + (chartCount % 2) * (chartWidth + 10);

              // Chart container with border
              pdf.setFillColor(255, 255, 255);
              pdf.rect(
                chartX - 2,
                yPosition - 2,
                chartWidth + 4,
                chartHeight + 15,
                "F"
              );
              pdf.setDrawColor(229, 231, 235);
              pdf.setLineWidth(0.5);
              pdf.rect(
                chartX - 2,
                yPosition - 2,
                chartWidth + 4,
                chartHeight + 15
              );

              // Chart title with better styling
              const titleElement = container.querySelector("h3");
              if (titleElement) {
                pdf.setFontSize(11);
                pdf.setTextColor(21, 42, 83);
                const title =
                  titleElement.textContent || `Chart ${chartCount + 1}`;
                pdf.text(title.substring(0, 30), chartX, yPosition - 8);
              }

              // Add chart image
              pdf.addImage(
                chartDataUrl,
                "PNG",
                chartX,
                yPosition,
                chartWidth,
                chartHeight
              );
              chartCount++;
            }
          }
          if (chartCount > 0) yPosition += 80;
        } else {
          // Enhanced fallback message
          pdf.setFillColor(254, 243, 199);
          pdf.rect(margin, yPosition, contentWidth, 20, "F");
          pdf.setTextColor(146, 64, 14);
          pdf.setFontSize(11);
          pdf.text(
            "Interactive charts available in the online version",
            margin + 10,
            yPosition + 12
          );
          yPosition += 30;
        }
      } catch (error) {
        console.warn("Chart capture error:", error);
        pdf.setFillColor(254, 243, 199);
        pdf.rect(margin, yPosition, contentWidth, 20, "F");
        pdf.setTextColor(146, 64, 14);
        pdf.setFontSize(11);
        pdf.text(
          "Interactive charts available in the online version",
          margin + 10,
          yPosition + 12
        );
        yPosition += 30;
      }

      // Start new page for Priority Recommendations
      pdf.addPage();
      yPosition = margin;

      // Enhanced Recommendations Section

      // Section header with icon
      pdf.setFillColor(248, 250, 252);
      pdf.rect(margin, yPosition - 5, contentWidth, 25, "F");
      pdf.setFillColor(201, 167, 80);
      pdf.rect(margin, yPosition - 5, 4, 25, "F");

      pdf.setTextColor(21, 42, 83);
      pdf.setFontSize(18);
      pdf.text("Priority Recommendations", margin + 10, yPosition + 8);

      pdf.setTextColor(75, 85, 99);
      pdf.setFontSize(10);
      pdf.text(
        "Strategic actions ranked by impact and urgency",
        margin + 10,
        yPosition + 16
      );
      yPosition += 35;

      const sortedRecommendations = recommendations.sort((a, b) => {
        const priorityOrder = { Critical: 4, High: 3, Medium: 2, Low: 1 };
        return priorityOrder[b.priority] - priorityOrder[a.priority];
      });

      for (const [index, rec] of sortedRecommendations.slice(0, 5).entries()) {
        yPosition = checkPageBreak(yPosition, 45);

        // Recommendation card background
        pdf.setFillColor(255, 255, 255);
        pdf.rect(margin, yPosition - 5, contentWidth, 40, "F");
        pdf.setDrawColor(229, 231, 235);
        pdf.setLineWidth(0.5);
        pdf.rect(margin, yPosition - 5, contentWidth, 40);

        // Priority badge with enhanced styling
        const priorityColor =
          rec.priority === "Critical"
            ? [220, 38, 38]
            : rec.priority === "High"
            ? [239, 68, 68]
            : [245, 158, 11];

        pdf.setFillColor(priorityColor[0], priorityColor[1], priorityColor[2]);
        pdf.roundedRect(margin + 5, yPosition - 2, 25, 10, 2, 2, "F");
        pdf.setTextColor(255, 255, 255);
        pdf.setFontSize(8);
        pdf.text(rec.priority, margin + 17.5, yPosition + 4, {
          align: "center",
        });

        // Title with improved typography
        pdf.setTextColor(21, 42, 83);
        pdf.setFontSize(13);
        const titleLines = pdf.splitTextToSize(rec.title, contentWidth - 40);
        pdf.text(titleLines, margin + 35, yPosition + 3);

        // Description with better spacing
        pdf.setTextColor(55, 65, 81);
        pdf.setFontSize(9);
        const descLines = pdf.splitTextToSize(
          rec.description,
          contentWidth - 15
        );
        pdf.text(descLines, margin + 5, yPosition + 15);

        yPosition += 50;
      }

      // Start new page for Why Audit Section
      pdf.addPage();
      yPosition = margin;

      // Enhanced Why Audit Section

      // Premium section header
      pdf.setFillColor(21, 42, 83);
      pdf.rect(margin, yPosition, contentWidth, 20, "F");
      pdf.setFillColor(201, 167, 80);
      pdf.rect(margin, yPosition + 15, contentWidth, 5, "F");

      pdf.setTextColor(255, 255, 255);
      pdf.setFontSize(16);
      pdf.text(
        "Why Choose Our 3-Day Business Audit?",
        pageWidth / 2,
        yPosition + 13,
        { align: "center" }
      );
      yPosition += 35;

      // Value proposition box
      pdf.setFillColor(248, 250, 252);
      pdf.rect(margin, yPosition, contentWidth, 25, "F");
      pdf.setDrawColor(201, 167, 80);
      pdf.setLineWidth(1);
      pdf.rect(margin, yPosition, contentWidth, 25);

      pdf.setTextColor(21, 42, 83);
      pdf.setFontSize(14);
      pdf.text(
        "Comprehensive Business Transformation",
        pageWidth / 2,
        yPosition + 12,
        { align: "center" }
      );

      pdf.setTextColor(75, 85, 99);
      pdf.setFontSize(10);
      pdf.text(
        "Unlock your business potential with our proven IN.8 Framework™",
        pageWidth / 2,
        yPosition + 20,
        { align: "center" }
      );
      yPosition += 35;

      const auditBenefits = [
        "• Complete clarity on what's holding your business back",
        "• Prioritized roadmap for maximum impact",
        "• Quick wins for immediate results",
        "• Strategic vision for sustainable growth",
        "• Expert external perspective on blind spots",
        "• FREE IN.8 Snapshot™ report (worth $2,500)",
      ];

      pdf.setTextColor(55, 65, 81);
      pdf.setFontSize(11);
      auditBenefits.forEach((benefit, index) => {
        yPosition = checkPageBreak(yPosition, 12);

        // Benefit item with enhanced styling
        pdf.setFillColor(255, 255, 255);
        pdf.rect(margin, yPosition - 2, contentWidth, 10, "F");
        pdf.setDrawColor(229, 231, 235);
        pdf.setLineWidth(0.2);
        pdf.rect(margin, yPosition - 2, contentWidth, 10);

        // Benefit icon and text
        const benefitLines = pdf.splitTextToSize(benefit, contentWidth - 15);
        pdf.text(benefitLines, margin + 5, yPosition + 4);
        yPosition += benefitLines.length * 4 + 8;
      });

      // Enhanced Call to Action
      yPosition = checkPageBreak(yPosition, 50);

      // CTA background with gradient effect simulation
      pdf.setFillColor(21, 42, 83);
      pdf.rect(margin, yPosition, contentWidth, 45, "F");

      // Gold accent border
      pdf.setFillColor(201, 167, 80);
      pdf.rect(margin, yPosition, contentWidth, 3, "F");
      pdf.rect(margin, yPosition + 42, contentWidth, 3, "F");

      // CTA content with better typography
      pdf.setTextColor(255, 255, 255);
      pdf.setFontSize(16);
      pdf.text(
        "Ready to Unlock Your Growth Potential?",
        pageWidth / 2,
        yPosition + 15,
        { align: "center" }
      );

      pdf.setFontSize(12);
      pdf.setTextColor(201, 167, 80);
      pdf.text(
        "Book Your FREE 3-Day Business Audit Today",
        pageWidth / 2,
        yPosition + 25,
        { align: "center" }
      );

      pdf.setFontSize(14);
      pdf.setTextColor(255, 255, 255);
      pdf.text(
        "Visit: intheraconsultinggroup.com",
        pageWidth / 2,
        yPosition + 35,
        {
          align: "center",
        }
      );
      pdf.link(margin, yPosition, contentWidth, 35, {
        url: "https://intheraconsultinggroup.com/",
      });
      yPosition += 45;

      // Start new page for Your Responses Section
      pdf.addPage();
      yPosition = margin;

      // Enhanced Your Responses Section

      // Section header with icon
      pdf.setFillColor(248, 250, 252);
      pdf.rect(margin, yPosition - 5, contentWidth, 25, "F");
      pdf.setFillColor(201, 167, 80);
      pdf.rect(margin, yPosition - 5, 4, 25, "F");

      pdf.setTextColor(21, 42, 83);
      pdf.setFontSize(18);
      pdf.text("Your Assessment Responses", margin + 10, yPosition + 8);

      pdf.setTextColor(75, 85, 99);
      pdf.setFontSize(10);
      pdf.text(
        "Complete record of your business assessment",
        margin + 10,
        yPosition + 16
      );
      yPosition += 35;

      pdf.setTextColor(0, 0, 0);
      pdf.setFontSize(9);
      results.forEach((result, index) => {
        yPosition = checkPageBreak(yPosition, 35);

        // Enhanced question card design
        pdf.setFillColor(255, 255, 255);
        pdf.rect(margin, yPosition - 5, contentWidth, 30, "F");
        pdf.setDrawColor(229, 231, 235);
        pdf.setLineWidth(0.5);
        pdf.rect(margin, yPosition - 5, contentWidth, 30);

        // Question number badge
        pdf.setFillColor(201, 167, 80);
        pdf.roundedRect(margin + 5, yPosition - 2, 15, 8, 2, 2, "F");
        pdf.setTextColor(255, 255, 255);
        pdf.setFontSize(9);
        pdf.text(`Q${index + 1}`, margin + 12.5, yPosition + 3, {
          align: "center",
        });

        // Question text with improved styling
        pdf.setTextColor(21, 42, 83);
        pdf.setFontSize(10);
        const questionLines = pdf.splitTextToSize(
          result.question,
          contentWidth - 30
        );
        pdf.text(questionLines, margin + 25, yPosition + 2);

        // Answer section with highlight
        pdf.setFillColor(248, 250, 252);
        pdf.rect(margin + 5, yPosition + 12, contentWidth - 10, 10, "F");

        pdf.setTextColor(75, 85, 99);
        pdf.setFontSize(8);
        pdf.text("Your Answer:", margin + 8, yPosition + 16);

        pdf.setTextColor(21, 42, 83);
        pdf.setFontSize(9);
        const answerLines = pdf.splitTextToSize(
          result.answer,
          contentWidth - 20
        );
        pdf.text(answerLines, margin + 8, yPosition + 19);

        yPosition += 35;
      });

      // Add footers
      const totalPages = pdf.getNumberOfPages();
      for (let i = 1; i <= totalPages; i++) {
        pdf.setPage(i);
        addFooter();
      }

      pdf.save("Business-Audit-Assessment.pdf");

      if (button) {
        button.disabled = false;
        button.innerHTML = "Export Comprehensive Report";
      }
    } catch (error) {
      console.error("Error generating PDF:", error);
      const button = document.querySelector(
        "[data-pdf-export]"
      ) as HTMLButtonElement;
      if (button) {
        button.disabled = false;
        button.innerHTML = "Export Comprehensive Report";
      }
    }
  };

  return (
    <button
      onClick={exportToPDF}
      data-pdf-export
      style={{
        padding: "1rem 2rem",
        background: "rgb(201, 167, 80)",
        color: "white",
        border: "none",
        borderRadius: "12px",
        cursor: "pointer",
        fontSize: "1.1rem",
        fontWeight: "600",
        transition: "all 0.3s ease",
        boxShadow: "0 4px 15px rgba(201, 167, 80, 0.3)",
        display: "flex",
        alignItems: "center",
        gap: "0.5rem",
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.transform = "translateY(-2px)";
        e.currentTarget.style.boxShadow = "0 6px 20px rgba(201, 167, 80, 0.4)";
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.transform = "translateY(0)";
        e.currentTarget.style.boxShadow = "0 4px 15px rgba(201, 167, 80, 0.3)";
      }}
    >
      Export Comprehensive Report
    </button>
  );
};

export default BusinessAuditPDFExport;
